/**
 * Velocity tone for sampled instruments: a real instrument played softly is
 * darker as well as quieter. A single recording stretched over every
 * velocity only gets quieter, so soft notes sound like a loud note turned
 * down. This low-pass opens with velocity and is placed relative to the
 * note's pitch, so the same velocity sounds alike across the keyboard. At
 * full velocity it is out of the way.
 */
export const VELOCITY_TONE_DEFAULT = 0.6;
// The softest note keeps at least this many octaves above its fundamental.
const MIN_OCTAVES = 2.5;
const CURVE = 0.8;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Low-pass cutoff (Hz) for a played MIDI pitch and velocity (0–1), or null
 * when no filter is needed (amount 0, full velocity, or a note so high its
 * open band is already narrower than the minimum).
 */
export function velocityToneCutoff(pitch, velocity, amount, sampleRate) {
  const depth = clamp(Number(amount) || 0, 0, 1);
  if (depth === 0) return null;
  const fundamental = 440 * 2 ** ((pitch - 69) / 12);
  const ceiling = Math.min(20000, (sampleRate || 48000) * 0.45);
  const open = Math.log2(ceiling / fundamental);
  if (!(open > MIN_OCTAVES)) return null;
  const target = MIN_OCTAVES + (open - MIN_OCTAVES) * clamp(Number(velocity) || 0, 0, 1) ** CURVE;
  const cutoff = fundamental * 2 ** (open - (open - target) * depth);
  return cutoff >= ceiling * 0.98 ? null : cutoff;
}

/** Zones already split by velocity carry their own dynamics; leave them alone. */
export function hasVelocityLayers(zones = []) {
  return zones.some((zone) => zone.trigger !== 'release' && (zone.loVelocity > 0 || zone.hiVelocity < 1));
}
